import { useMemo } from 'react';
import { ArrowUp } from 'lucide-react';
import { trackingApi } from '../api/tracking';
import { ApiError } from '../api/client';
import { SectionHeader } from './SectionHeader';
import { useQuery } from '@tanstack/react-query';
import { AuditLogEntry } from '../types/admin';

export interface StageTransition {
  from: string;
  to: string;
  task: string;
}

/** action name (as actually logged by auditEngine.log() in that entity's controller) -> stage transition. */
export type ActionStageMap = Record<string, StageTransition>;

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function extractComment(value: unknown): string | null {
  if (value && typeof value === 'object' && 'comment' in value) {
    const c = (value as Record<string, unknown>).comment;
    return typeof c === 'string' && c.trim() ? c : null;
  }
  return null;
}

interface WorkflowTrackingTabProps {
  entityType: string;
  entityId?: number;
  /** Static translation for entities whose controllers log semantic action names (e.g. 'submitted', 'request_approved') rather than a real status pair. */
  actionStageMap?: ActionStageMap;
  /** Pretty labels for entities that DO log real old_values.status/new_values.status pairs (e.g. vacancy, grant) - checked first, since it's real data rather than a static guess. */
  statusLabels?: Record<string, string>;
}

function extractStatus(value: unknown): string | null {
  if (value && typeof value === 'object' && 'status' in value) {
    const s = (value as Record<string, unknown>).status;
    return typeof s === 'string' ? s : null;
  }
  return null;
}

interface ResolvedTransition extends StageTransition {
  entry: AuditLogEntry;
}

function resolveTransitions(entries: AuditLogEntry[], actionStageMap: ActionStageMap, statusLabels: Record<string, string>): ResolvedTransition[] {
  const resolved: ResolvedTransition[] = [];
  for (const entry of entries) {
    const fromStatus = extractStatus(entry.old_values);
    const toStatus = extractStatus(entry.new_values);
    if (fromStatus && toStatus && fromStatus !== toStatus) {
      resolved.push({ from: statusLabels[fromStatus] ?? fromStatus, to: statusLabels[toStatus] ?? toStatus, task: `Move to ${statusLabels[toStatus] ?? toStatus}`, entry });
      continue;
    }
    const cfg = actionStageMap[entry.action];
    if (cfg) resolved.push({ ...cfg, entry });
  }
  return resolved;
}

/**
 * Boxed stage-history view matching the OneGMS reference exactly:
 * current-stage box, centered timestamp (hover reveals who made the
 * change and their comment - both real, from the audit log and the
 * decision's own recorded comment where one exists), next-stage box,
 * connected by an upward arrow. Contained in a fixed-height scrollable
 * box rather than growing the page, per explicit direction.
 *
 * Deliberately does NOT show an email icon or count - there is no real
 * notification backend yet, and a decorative icon that implies an email
 * was sent when none was would be misleading. Add it once that backend
 * exists.
 *
 * Two real data shapes exist across VConnect's controllers, found by
 * reading them directly rather than assuming one pattern everywhere:
 * some (vacancy, grant) log a genuine old_values.status/new_values.status
 * pair - resolveTransitions() prefers this real diff whenever both are
 * present. Most others (workforce_request, leave_request, journal_entry,
 * ...) only log a semantic action name ('submitted', 'request_approved'),
 * so actionStageMap is the static translation layer for those - the same
 * category as an existing STATUS_LABELS lookup, not fabricated per-record
 * data. Entries matching neither (field edits, document uploads,
 * comments) are intentionally omitted - this view is stage history
 * specifically, matching what the OneGMS reference itself shows.
 */
export function WorkflowTrackingTab({ entityType, entityId, actionStageMap = {}, statusLabels = {} }: WorkflowTrackingTabProps) {
  const isMine = entityType === 'mine';
  const query = useQuery({
    queryKey: ['tracking', entityType, entityId],
    queryFn: () => (isMine ? trackingApi.mine() : trackingApi.forEntity(entityType, entityId as number)),
    enabled: isMine || entityId !== undefined,
  });

  const entries = useMemo<AuditLogEntry[]>(() => query.data?.data ?? [], [query.data]);
  const stageEntries = useMemo(() => resolveTransitions(entries, actionStageMap, statusLabels), [entries, actionStageMap, statusLabels]);
  const forbidden = query.error instanceof ApiError && query.error.status === 403;

  return (
    <div className="panel">
      <SectionHeader title="Tracking" />
      <div className="panel-body">
        {query.isLoading && <p className="muted">Loading tracking history…</p>}
        {forbidden && <p className="muted">You do not have permission to view tracking for this record.</p>}
        {!query.isLoading && !forbidden && stageEntries.length === 0 && <p className="muted">No stage history yet.</p>}
        {!query.isLoading && !forbidden && stageEntries.length > 0 && (
          <div className="workflow-tracking-scroll">
            {stageEntries.map((t, i) => {
              const comment = extractComment(t.entry.new_values);
              const tooltip = `${t.entry.actor_name ?? 'System'}${comment ? ` — ${comment}` : ''}`;
              return (
                <div key={t.entry.id}>
                  <div className="workflow-stage-row">
                    <div className="workflow-stage-box">{t.from}</div>
                    <div className="workflow-stage-center">
                      <span className="workflow-stage-timestamp" title={tooltip}>{formatDateTime(t.entry.created_at)}</span>
                      <div className="workflow-stage-task">Task: {t.task}</div>
                    </div>
                    <div className="workflow-stage-box">{t.to}</div>
                  </div>
                  {i < stageEntries.length - 1 && (
                    <div className="workflow-stage-arrow"><ArrowUp size={16} aria-hidden="true" /></div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
